import { isAssignedToCoach } from '../../../components/only-mine-filter'
import React, { useContext, useState, useMemo, useEffect } from "react";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import { AuthContext } from "../../../../../auth-context";
import { DashboardContext } from "../../../dashboard-context";
import { Row, Action } from "../../../../../components/action-button";
import DeleteIcon from "@material-ui/icons/DeleteForever";
import { deleteSurveyAssignment, loadProfile } from "../../../../../api";
import { ReactComponent as ArrowUp } from "../../../../../images/arrow-up.svg";
import { ReactComponent as ArrowDown } from "../../../../../images/arrow-down.svg";
import moment from "moment";
import AssignmentIcon from "@material-ui/icons/Assignment";
import SearchIcon from "@material-ui/icons/Search";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableHeadRow,
  TableBodyRow,
  HeadCell,
  DataCell,
  BulkEdit,
  BulkAction,
  WhiteCheckbox,
} from "../../../../../components/table";

const ArrowsGroup = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const Description = styled.p`
  margin: 0 0 16px 0;
  color: #555;
  font-family: "DM Sans", sans-serif;
  font-size: 1rem;
  line-height: 1.4;
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  margin: 10px auto 10px auto;
  padding-bottom: 8px;
  color: #181818;
  font-family: "DM Sans", sans-serif;
  font-size: 1.25rem;
  font-weight: 600;

  button {
    margin-left: 16px;
  }
`;

const AssignedClients = ({
  onlyMine = false,
  history,
  setShowResponseModal,
  setSelectedUserID,
}) => {
  const { user, updateUser } = useContext(AuthContext);
  const { openSnackbar } = useContext(DashboardContext);
  const [sortType, setSortType] = useState({ type: "name", reverse: false });
  const [showSelect, setShowSelect] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const assignedClients = useMemo(() => {
    const clients = user.accounts.filter((account) =>
      account?.survey?.assigned_survey_id && (!onlyMine || isAssignedToCoach(account, user.id))
    );
    clients.sort((a, b) => (a[sortType.type] || '').localeCompare(b[sortType.type] || ''));
    return sortType.reverse ? clients.reverse() : clients;
  }, [user.accounts, user.id, onlyMine, sortType]);
  const visibleIDs = useMemo(() => new Set(assignedClients.map((client) => client.id)), [assignedClients]);
  const visibleSelectedClients = selectedClients.filter((id) => visibleIDs.has(id));

  useEffect(() => {
    setSelectedClients((selected) => {
      const visible = selected.filter((id) => visibleIDs.has(id));
      return visible.length === selected.length ? selected : visible;
    });
  }, [visibleIDs]);

  useEffect(() => { setSelectedClients([]); }, [user.id, user.owner_id]);

  const handleDelete = (accountID) => {
    deleteSurveyAssignment(user.id, [accountID])
      .then((res) => {
        openSnackbar("Client removed from check-in form", false);
        loadProfile(user.id).then((res) => {
          updateUser(res.data.profile);
        });
      })
      .catch((err) => {
        openSnackbar("Error removing client from check-in form", true);
      });
  };

  const handleDeleteBulk = () => {
    if (visibleSelectedClients.length === 0) return;
    deleteSurveyAssignment(user.id, visibleSelectedClients)
      .then((res) => {
        setSelectedClients([]);
        openSnackbar("Clients removed from check-in form", false);
        loadProfile(user.id).then((res) => {
          updateUser(res.data.profile);
        });
      })
      .catch((err) => {
        openSnackbar("Error removing client from check-in form", true);
      });
  };

  const handleInspect = (clientID) => {
    history.push(`/dashboard/inspect/${clientID}/overview`);
  };

  const renderSortArrows = (isSorting, reverse) => {
    return (
      <div style={{ display: "inline-block", marginLeft: "8px" }}>
        <ArrowsGroup>
          <ArrowUp fill={isSorting && reverse ? "#FFFFFF" : "#999999"} />
          <div style={{ height: "3px" }}></div>
          <ArrowDown fill={isSorting && !reverse ? "#FFFFFF" : "#999999"} />
        </ArrowsGroup>
      </div>
    );
  };

  const handleSort = (type) => {
    if (type !== sortType.type) {
      setSortType({ type: type, reverse: false });
    } else {
      setSortType({ type: type, reverse: !sortType.reverse });
    }
  };

  const handleSelect = (user_id) => {
    if (selectedClients.includes(user_id)) {
      setSelectedClients(selectedClients.filter((id) => id !== user_id));
    } else {
      setSelectedClients([...selectedClients, user_id]);
    }
  };

  const handleSelectAll = () => {
    setSelectedClients(visibleSelectedClients.length === assignedClients.length
      ? [] : assignedClients.map((client) => client.id));
  };

  return (
    <>
      <Title style={{ marginTop: "42px" }}>
        Clients with Check-in Forms
        <BulkEdit
          highlight={showSelect}
          onClick={() => {
            setShowSelect(!showSelect);
            setSelectedClients([]);
          }}
        >
          Bulk Edit
        </BulkEdit>
        {showSelect && (
          <BulkAction
            onClick={handleDeleteBulk}
          >
            Delete check-in form assignment(s)
          </BulkAction>
        )}
      </Title>
      <Description>
        A client can only have one check-in form assigned at a time. Assigning another check-in form will overwrite their current check-in form.
      </Description>
      <TableContainer>
        <Table>
          <TableHead>
            <TableHeadRow>
              {showSelect && (
                <HeadCell>
                  <WhiteCheckbox
                    style={{ color: "#FFF" }}
                    checked={assignedClients.length > 0 && visibleSelectedClients.length === assignedClients.length}
                    onChange={handleSelectAll}
                    inputProps={{ "aria-label": "checkbox" }}
                  />
                </HeadCell>
              )}
              <HeadCell onClick={() => handleSort("email_address")}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  Email Address
                  {renderSortArrows(
                    sortType.type === "email_address",
                    sortType.reverse
                  )}
                </div>
              </HeadCell>
              <HeadCell onClick={() => handleSort("name")}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  Name
                  {renderSortArrows(sortType.type === "name", sortType.reverse)}
                </div>
              </HeadCell>
              <HeadCell>Assigned Check-in Form</HeadCell>
              <HeadCell>Last Response</HeadCell>
              <HeadCell style={{ textAlign: "center" }}>Actions</HeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {assignedClients.length === 0 && <TableBodyRow><DataCell colSpan={showSelect ? 6 : 5}>No clients with check-in forms match this filter.</DataCell></TableBodyRow>}
            {assignedClients.map((client) => {
              return (
                <TableBodyRow key={client.id}>
                  {showSelect && (
                    <DataCell>
                      <WhiteCheckbox
                        checked={selectedClients.includes(client.id)}
                        onChange={() => handleSelect(client.id)}
                        inputProps={{ "aria-label": "checkbox" }}
                      />
                    </DataCell>
                  )}
                  <DataCell onClick={() => handleSort("email_address")}>
                    {client.email_address}
                  </DataCell>
                  <DataCell onClick={() => handleSort("name")}>
                    {client.name}
                  </DataCell>
                  <DataCell>{client.survey?.assigned_survey_name}</DataCell>
                  <DataCell>
                    {client.survey?.recent_survey_created_at
                      ? moment
                          .utc(client.survey?.recent_survey_created_at)
                          .format("MMM D, YYYY")
                      : "N/A"}
                  </DataCell>
                  <DataCell
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                    <Row>
                      <Action
                        disabled={!client.survey?.recent_survey_created_at}
                        icon={<AssignmentIcon />}
                        text="Responses"
                        onClick={() => {
                          if (
                            client.user_id &&
                            client.survey?.recent_survey_created_at
                          ) {
                            setShowResponseModal(true);
                            setSelectedUserID(client.user_id);
                          } else {
                            openSnackbar(
                              "Client has not completed a check-in form",
                              true
                            );
                          }
                        }}
                      />
                      {client.claimed === 1 && (
                        <Action
                          icon={<SearchIcon />}
                          text="Inspect"
                          onClick={() => handleInspect(client.id)}
                        />
                      )}
                      <Action
                        icon={<DeleteIcon />}
                        iconColor="#da0f0f"
                        text="Delete"
                        onClick={() => handleDelete(client.id)}
                      />
                    </Row>
                  </DataCell>
                </TableBodyRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default withRouter(AssignedClients);
